import type { Pairing } from '@/common/db-typebox';
import type { MenuWithPairing } from '@/common/domain-model';
import { clientStore } from '@/lib/client';
import { message } from 'antd';
import { makeAutoObservable } from 'mobx';
import type { PairingOrMenu } from '../types';
export class PairingStore {
  currentSelection: PairingOrMenu | null = null;
  pairingHistories: Pairing[] = [];
  menus: MenuWithPairing[] = [];
  isLoading = false;
  activeTab: 'history' | 'menu' = 'menu';
  isPairingFormVisible = false;
  isRetrying = false;

  constructor() {
    makeAutoObservable(this);
  }

  setCurrentSelection = (item: PairingOrMenu | null) => {
    this.currentSelection = item;
  };

  setPairingHistories = (histories: Pairing[]) => {
    this.pairingHistories = histories;
  };

  setMenus = (menus: MenuWithPairing[]) => {
    this.menus = menus;
  };

  setIsLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  setActiveTab = (tab: 'history' | 'menu') => {
    this.activeTab = tab;
  };

  setPairingFormVisible = (visible: boolean) => {
    this.isPairingFormVisible = visible;
  };

  setRetrying = (retrying: boolean) => {
    this.isRetrying = retrying;
  };

  handleSelect = (item: PairingOrMenu | undefined) => {
    if (!item) {
      this.setCurrentSelection(null);
      this.setPairingFormVisible(false);
      return;
    }
    this.setCurrentSelection(item);
    this.setPairingFormVisible(false);
  };

  handleAddPairing = () => {
    this.setCurrentSelection(null);
    this.setPairingFormVisible(true);
  };

  onSuccess = async (id: string) => {
    this.setPairingFormVisible(false);
    await this.refreshAndSelect(id);
  };

  retryPairing = async () => {
    if (!this.currentSelection) return;

    const pairingId =
      this.currentSelection.type === 'menu'
        ? this.currentSelection.menu.pairing?.id
        : this.currentSelection.pairing.id;

    if (!pairingId) return;

    this.setRetrying(true);
    try {
      await clientStore.treatyClient.api.pairing
        .retry({
          id: pairingId,
        })
        .post();

      this.setCurrentSelection(null);
      message.success('Processing started');
    } catch (err) {
      console.error(err);
      message.error('Failed to retry, please try again later');
    } finally {
      await this.onSuccess(pairingId);
      this.setRetrying(false);
    }
  };

  fetchPairingData = async (
    callback?: (data: Pairing[] | MenuWithPairing[]) => void,
  ) => {
    this.setIsLoading(true);
    try {
      if (this.activeTab === 'history') {
        const response =
          await clientStore.treatyClient.api.pairing.history.get();
        if (response.data?.success) {
          this.setPairingHistories(response.data.data);
          callback?.(response.data.data);
        }
      } else {
        const response = await clientStore.treatyClient.api.menu.list.get();
        if (response.data?.success) {
          this.setMenus(response.data.data);
          callback?.(response.data.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      this.setIsLoading(false);
    }
  };

  refreshAndSelect = async (id: string) => {
    await this.fetchPairingData((data) => {
      if (this.activeTab === 'history') {
        const newPairing = (data as Pairing[]).find((p) => p.id === id);
        if (newPairing) {
          this.setCurrentSelection({
            type: 'pairing',
            pairing: newPairing,
          });
        }
      } else {
        const newMenu = (data as MenuWithPairing[]).find(
          (m) => m.pairing?.id === id,
        );
        if (newMenu) {
          this.setCurrentSelection({
            type: 'menu',
            menu: newMenu,
          });
        }
      }
    });
  };

  refreshCurrentPairing = async () => {
    if (!this.currentSelection) return;

    const pairingId =
      this.currentSelection.type === 'menu'
        ? this.currentSelection.menu.pairing?.id
        : this.currentSelection.pairing.id;

    if (pairingId) {
      await this.refreshAndSelect(pairingId);
    }
  };

  togglePublic = async (pairingIds: string[]) => {
    try {
      const response = await clientStore.treatyClient.api.pairing[
        'toggle-public'
      ].post({
        ids: pairingIds,
      });

      if (response.data?.success) {
        const results = response.data.results;
        const allPublic = results.every((r) => r.public);
        const allPrivate = results.every((r) => !r.public);

        if (allPublic) {
          message.success('All pairings are now public');
        } else if (allPrivate) {
          message.success('All pairings are now private');
        } else {
          message.success('Pairing visibility updated');
        }
        return;
      }
      message.error(
        (response.data as any)?.message || 'Failed to toggle public status',
      );
    } catch (err) {
      console.error(err);
      message.error('Failed to toggle public status');
    }
  };
}

export const pairingStore = new PairingStore();
